import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Phone,
  ChevronDown,
  MapPin,
  Star,
  DollarSign,
  HelpCircle,
  Users,
  ArrowRight,
  Home,
  TrendingUp,
  Menu,
  X,
  Package,
  Film,
  PlayCircle,
} from "lucide-react";

const novaAreas = [
  {
    label: "Alexandria",
    href: "/airbnb-management-alexandria",
    badge: "STR Friendly",
    badgeColor: "text-emerald-400",
  },
  {
    label: "Arlington",
    href: "/airbnb-management-arlington",
    badge: "High Demand",
    badgeColor: "text-blue-400",
  },
  {
    label: "Fairfax County",
    href: "/airbnb-management-fairfax",
    badge: "Cap: 60 nights",
    badgeColor: "text-amber-400",
  },
  {
    label: "Loudoun County",
    href: "/airbnb-management-loudoun",
    badge: "STR Friendly",
    badgeColor: "text-emerald-400",
  },
  {
    label: "Prince William",
    href: "/airbnb-management-prince-william",
    badge: "STR Friendly",
    badgeColor: "text-emerald-400",
  },
];

const dcAreas = [
  {
    label: "Washington, D.C.",
    href: "/airbnb-management-washington-dc",
    badge: "Regulated",
    badgeColor: "text-amber-400",
  },
];

const serviceAreas = [...novaAreas, ...dcAreas];

const navLinks = [
  { label: "Home", icon: Home, href: "/" },
  { label: "Services", icon: Star, href: "/services" },
  { label: "Pricing", icon: DollarSign, href: "/pricing" },
  { label: "Calculator", icon: TrendingUp, href: "/calculator" },
  { label: "Furniture", icon: Package, href: "/furniture" },
  { label: "Resources", icon: HelpCircle, href: "/blog" },
  { label: "Watch", icon: PlayCircle, href: "/videos" },
  { label: "About", icon: Users, href: "/about" },
];

const Navbar = () => {
  const [areasOpen, setAreasOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isActive = (href: string) =>
    href === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(href);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setAreasOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const scrollToForm = () => {
    setMobileOpen(false);
    const el = document.getElementById("lead-form");
    if (el) {
      window.dispatchEvent(new Event("force-load-form"));
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border/40">
      <div className="container mx-auto px-4">
        <div className="h-16 md:h-18 flex items-center justify-between gap-4">
          {/* Logo */}
          <a href="/" className="flex items-center shrink-0 group">
            <img
              src="https://vibe.filesafe.space/1778679421893490393/attachments/45d9bd93-e98b-4f65-bfcf-0d98c7cfc665.png"
              alt="JNN Homes Logo"
              className="h-8 md:h-10 w-auto object-contain"
            />
            {/* Mobile-only tagline to fill space */}
            <div className="lg:hidden ml-2 pl-2 border-l border-border/40 flex flex-col items-center leading-none">
              <span className="text-[0.45rem] font-semibold uppercase tracking-[0.15em] text-muted-foreground/55 whitespace-nowrap">
                Real Estate
              </span>
              <span className="mt-0.5 text-[0.5rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70 whitespace-nowrap">
                Investment · Management
              </span>
              <span className="mt-0.5 text-[0.45rem] font-semibold uppercase tracking-[0.15em] text-muted-foreground/55 whitespace-nowrap">
                Development
              </span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(({ label, icon: Icon, href }) => {
              const active = isActive(href);
              return (
                <a
                  key={label}
                  href={href}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 group ${
                    active
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <Icon
                    className={`w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity ${active ? "opacity-100" : ""}`}
                  />
                  {label}
                </a>
              );
            })}

            {/* Service Areas Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setAreasOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-body font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200 group"
              >
                <MapPin className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                Service Areas
                <ChevronDown
                  className={`w-3.5 h-3.5 opacity-70 transition-transform duration-200 ${areasOpen ? "rotate-180" : ""}`}
                />
              </button>

              {areasOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 glass border border-border/60 rounded-xl shadow-2xl shadow-black/20 overflow-hidden z-50 origin-top animate-in fade-in-0 zoom-in-95 duration-200">
                  <div className="p-2">
                    <div className="px-3 py-2 mb-1">
                      <p className="text-xs font-body font-semibold text-muted-foreground uppercase tracking-wider">
                        Northern Virginia
                      </p>
                    </div>
                    {novaAreas.map((area) => (
                      <a
                        key={area.label}
                        href={area.href}
                        className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group"
                        onClick={() => setAreasOpen(false)}
                      >
                        <div className="flex items-center gap-2.5">
                          <MapPin className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-sm font-body font-medium text-foreground">
                            {area.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs font-body font-semibold ${area.badgeColor}`}
                          >
                            {area.badge}
                          </span>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-200" />
                        </div>
                      </a>
                    ))}
                    <div className="px-3 py-2 mb-1 mt-2 border-t border-border/60">
                      <p className="text-xs font-body font-semibold text-muted-foreground uppercase tracking-wider">
                        Washington, D.C.
                      </p>
                    </div>
                    {dcAreas.map((area) => (
                      <a
                        key={area.label}
                        href={area.href}
                        className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-primary/5 transition-colors group"
                        onClick={() => setAreasOpen(false)}
                      >
                        <div className="flex items-center gap-2.5">
                          <MapPin className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-sm font-body font-medium text-foreground">
                            {area.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs font-body font-semibold ${area.badgeColor}`}
                          >
                            {area.badge}
                          </span>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-200" />
                        </div>
                      </a>
                    ))}
                  </div>
                  <div className="border-t border-border/60 p-3 bg-muted/20">
                    <button
                      onClick={scrollToForm}
                      className="w-full flex items-center justify-center gap-2 py-2 text-sm font-body font-semibold text-primary hover:text-primary/80 transition-colors"
                    >
                      Check My Property's Income
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side — Phone + CTA + Hamburger */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <a
              href="tel:7038905425"
              className="flex items-center hover:text-primary transition-colors group"
              aria-label="Call JNN Homes"
              title="Call (703) 890-5425"
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Phone className="w-4 h-4 text-primary" />
              </div>
            </a>

            <Button
              variant="default"
              onClick={scrollToForm}
              className="hidden md:flex bg-primary text-primary-foreground hover:bg-primary/90 font-body font-bold text-sm px-4 h-9 rounded-lg"
            >
              See My Airbnb Income
            </Button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted/50 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X className="w-5 h-5 text-foreground" />
              ) : (
                <Menu className="w-5 h-5 text-foreground" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 border-t border-border/40 ${
          mobileOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-background/98 backdrop-blur-md px-4 py-4 space-y-1">
          {navLinks.map(({ label, icon: Icon, href }) => {
            const active = isActive(href);
            return (
              <a
                key={label}
                href={href}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-body font-medium transition-colors ${
                  active
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                <Icon className="w-4 h-4 text-primary" />
                {label}
              </a>
            );
          })}

          <div className="pt-1 pb-1">
            <p className="px-3 py-2 text-xs font-body font-semibold text-muted-foreground uppercase tracking-wider">
              Service Areas
            </p>
            {serviceAreas.map((area) => (
              <a
                key={area.label}
                href={area.href}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="text-sm font-body font-medium">
                    {area.label}
                  </span>
                </div>
                <span className={`text-xs font-semibold ${area.badgeColor}`}>
                  {area.badge}
                </span>
              </a>
            ))}
          </div>

          <div className="pt-3 border-t border-border/40 space-y-3">
            <a
              href="tel:7038905425"
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              aria-label="Call JNN Homes"
            >
              <Phone className="w-5 h-5" />
            </a>
            <Button
              onClick={scrollToForm}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-body font-bold h-11 rounded-lg"
            >
              See My Airbnb Income
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
